import { useEffect, useState } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { formatPln } from '../catalog/money';
import { markStyle, useContractorColors } from '../contractors/colors';
import { SupplierLacksItem } from '../subrentals/SupplierLacksItem';
import { QuickPicker, type PickerOption } from '../ui/QuickPicker';
import type { QuoteLine } from './types';

type Choice = { supplierId: string; supplierName: string; priceDayGrosze: number | null; /** owner 2026-10-24: „ma N szt.” (7a: only when seen) */ quantity?: number | null };
type ContractorOption = PickerOption & { id: string | null; text: string };
const OTHER = '__inny__';

/**
 * "Dostawca" of a line from a supplier (owner 2026-10-21 00:32): the suppliers whose catalog has this item (with their
 * price per day) and "Inny…" — any supplier, or a new one typed in. A change moves the line's rental to that supplier
 * ("Dopożyczenia"). Saved at once, no "are you sure". Owner 2026-10-23 20:59: a supplier without this item's position
 * is refused — „<Dostawca> nie ma tej pozycji.” with „Dodaj w katalogu dostawcy”; in "Inny…" such suppliers say so.
 */
export function SupplierCell({ line, editable, onSaved, onConflict }: { line: QuoteLine; editable: boolean; onSaved: (line: QuoteLine) => void; onConflict: () => void }) {
  const [choices, setChoices] = useState<Choice[] | null>(null);
  const [other, setOther] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [lacking, setLacking] = useState<{ supplierId: string; supplierName: string } | null>(null);
  const from = !!line.supplierId;
  const colors = useContractorColors();

  useEffect(() => {
    if (!from || !editable) return;
    let alive = true;
    api<{ suppliers: Choice[] }>(`/api/quotes/lines/${line.id}/suppliers`)
      .then((r) => alive && setChoices(r.suppliers ?? []))
      .catch(() => alive && setChoices([]));
    return () => {
      alive = false;
    };
  }, [from, editable, line.id, line.supplierId]);

  if (!from) {
    // owner 21:05: an own line rented for the event — its suppliers one under another (the row grows, nothing is cut)
    // owner 2026-10-24 (pkt 6): each name in its supplier's colour (the readable „-ink” shade)
    const names = [...new Map((line.rentals ?? []).map((r) => [r.supplierId, r.supplierName])).entries()];
    // owner 2026-11-01: a supplier whose rentals of the line were all cancelled with the event — struck through, tagged
    const cancelledOf = (id: string | null) => {
      const mine = (line.rentals ?? []).filter((r) => r.supplierId === id);
      return mine.length > 0 && mine.every((r) => r.mark?.kind === 'anulowane') ? mine[0]!.mark!.label : null;
    };
    return (
      <td className="q-supplier" data-label="Dostawca">
        {names.length > 0 && (
          <ul className="q-supplier-list" aria-label={`Dostawcy: ${line.name}`}>
            {names.map(([id, n]) => (
              <li key={id ?? `off:${n}`} className={`q-supplier-name${cancelledOf(id) ? ' q-supplier-cancelled' : ''}`} style={markStyle(id ? colors.get(id) : undefined)} title={cancelledOf(id) ?? undefined}>
                {n}
                {cancelledOf(id) && <span className="rental-mark rental-mark-anulowane"> {cancelledOf(id)}</span>}
              </li>
            ))}
          </ul>
        )}
      </td>
    );
  }

  async function move(supplierId: string, supplierName = '') {
    if (supplierId === line.supplierId) return;
    setBusy(true);
    setLacking(null);
    try {
      onSaved(await api<QuoteLine>(`/api/quotes/lines/${line.id}/supplier`, { method: 'PATCH', body: { supplierId, version: line.version } }));
      setError('');
      setOther(false);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'supplier_lacks_item') {
        // nothing changed: said so, with the way to that supplier's catalog
        setError('');
        setLacking({ supplierId, supplierName });
      } else if (e instanceof ApiError && e.status === 409) {
        setError('Ktoś w tym czasie zmienił tę pozycję. Pokazuję aktualne wartości.');
        onConflict();
      } else setError(e instanceof ApiError ? (fieldIssues(e)[0]?.message ?? e.message) : 'Nie udało się zmienić dostawcy');
    } finally {
      setBusy(false);
    }
  }

  async function searchSuppliers(q: string, signal: AbortSignal): Promise<ContractorOption[]> {
    const res = await api<{ contractors: Array<{ id: string; name: string }> }>(`/api/contractors?kind=supplier&q=${encodeURIComponent(q)}`, { signal });
    // suppliers that have this item first; the others stay selectable (then the message and „Dodaj w katalogu dostawcy”)
    // review M5: listed without a price = without the position (also the line's own supplier that lost it)
    const has = new Set((choices ?? []).filter((c) => c.priceDayGrosze !== null).map((c) => c.supplierId));
    const lacks = (id: string) => !!line.itemId && choices !== null && !has.has(id);
    const found = res.contractors
      .slice(0, 12)
      .map((c) => ({ key: c.id, id: c.id, name: c.name, details: lacks(c.id) ? ['nie ma tej pozycji'] : [], text: c.name }))
      .sort((a, b) => Number(lacks(a.id)) - Number(lacks(b.id)));
    const exact = found.some((c) => c.name.toLocaleLowerCase('pl') === q.trim().toLocaleLowerCase('pl'));
    return q.trim() !== '' && !exact ? [...found, { key: 'new', id: null, name: q.trim(), details: ['+ nowy dostawca'], text: q.trim() }] : found;
  }
  async function pickOther(o: ContractorOption) {
    // a new name: the contractor is added as a supplier first, then the line moves to it
    const id = o.id ?? (await api<{ id: string }>('/api/contractors', { method: 'POST', body: { kind: 'supplier', name: o.text } })).id;
    await move(id, o.text);
  }

  const list = choices ?? [];
  const current = list.some((c) => c.supplierId === line.supplierId) ? list : [{ supplierId: line.supplierId!, supplierName: line.supplierName ?? 'dostawca', priceDayGrosze: null }, ...list];
  return (
    <td className="q-supplier" data-label="Dostawca" data-feature-id="quotes.suppliers">
      {editable ? (
        <select
          className="cell-input q-supplier-name"
          aria-label={`Dostawca: ${line.name}`}
          value={line.supplierId!}
          disabled={busy}
          onChange={(e) => (e.target.value === OTHER ? setOther(true) : void move(e.target.value, current.find((c) => c.supplierId === e.target.value)?.supplierName))}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {current.map((c) => (
            <option key={c.supplierId} value={c.supplierId}>
              {c.priceDayGrosze !== null ? `${c.supplierName} · ${formatPln(c.priceDayGrosze)} / doba` : c.supplierName}
              {c.quantity != null ? ` · ma ${c.quantity} szt.` : ''}
            </option>
          ))}
          <option value={OTHER}>Inny…</option>
        </select>
      ) : (
        <span className="q-text q-supplier-name">{line.supplierName}</span>
      )}
      {other && (
        <div className="q-supplier-other" onKeyDown={(e) => e.stopPropagation()}>
          <QuickPicker<ContractorOption> label="Inny dostawca" placeholder="Wpisz dostawcę" search={searchSuppliers} onPick={pickOther} labelHidden floating />
          <button className="btn small ghost" type="button" onClick={() => setOther(false)}>
            Anuluj
          </button>
        </div>
      )}
      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
      {lacking && (
        <span className="field-error" role="alert">
          <SupplierLacksItem supplierId={lacking.supplierId} supplierName={lacking.supplierName} itemId={line.itemId} />
        </span>
      )}
    </td>
  );
}
